import { EntityProfileType, FirmSize } from "@common/types/entity";
import { FC, useCallback } from "react";
import { FormikProvider, useFormik } from "formik";
import { useCreateOrganizationMutation } from "@api/organization.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { Extends } from "@common/helpers/typescript";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { BaseOnboardingContainer } from "../components/BaseOnboardingContainer";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import { CreateOrganization } from "../steps/CreateOrganization";
import { OrganizationPreview } from "../components/OrganizationPreview/OrganizationPreview";
import {
    organizationSchema,
    bookkeepingStartDateAsValue,
    createOrganizationFormSchema,
    CreateOrganizationFormSchema,
    defaultDemoBookkeepingStartDate,
} from "../organizationOnboardingForms";
import { OnboardingStepKey } from "../steps";
import { useManageSteps } from "../useManageSteps";
import { OrganizationClientProfile } from "../steps/OrganizationClientProfile";
import { CreateOrganizationDataProvider } from "../components/CreateOrganizationDataProvider";

type CreateOrganizationFlowSteps = Extends<
    OnboardingStepKey,
    "createOrganization" | "organizationClientProfile"
>;

export const CreateOrganizationFlow: FC = () => {
    const { toast } = useToaster();
    const createOrganizationMutation = useCreateOrganizationMutation();
    const { changeActiveOrganization } = useScope();

    const { go, currentStep, nextStep, isExiting } =
        useManageSteps<CreateOrganizationFlowSteps>(
            true,
            () => "createOrganization",
        );

    const formik = useFormik<CreateOrganizationFormSchema>({
        initialValues: {
            organizationName: "",
            firmSize: "" as FirmSize,
            servicesOffered: [],
            businessName: "",
            legalType: "" as EntityProfileType,
            bookkeepingStartDate: bookkeepingStartDateAsValue(
                defaultDemoBookkeepingStartDate,
            ),
        },
        validate: validateFormikWithZodSchema(createOrganizationFormSchema),
        onSubmit: submitHelper({
            handler: async ({
                organizationName,
                firmSize,
                servicesOffered,
                businessName,
                legalType,
                bookkeepingStartDate,
            }) => {
                const organization =
                    await createOrganizationMutation.mutateAsync({
                        name: organizationName,
                        profile: {
                            firmSize,
                            servicesOffered,
                        },
                        client: {
                            name: businessName,
                            legalType,
                            bookkeepingStartDate,
                        },
                    });

                toast("Firm created");
                changeActiveOrganization(organization);
            },
        }),
    });

    const createStepValidator = useCallback(
        (values: CreateOrganizationFormSchema) =>
            organizationSchema.safeParse(values).success,
        [],
    );

    const sidebar = <OrganizationPreview />;

    let main: React.ReactNode;

    switch (currentStep) {
        case "createOrganization":
            main = (
                <CreateOrganization
                    next={() => go("organizationClientProfile")}
                    validator={createStepValidator}
                />
            );
            break;

        case "organizationClientProfile":
            main = (
                <OrganizationClientProfile
                    formBusy={formik.isSubmitting}
                    onBack={() => go("createOrganization")}
                />
            );
            break;
    }

    return (
        <BaseOnboardingContainer>
            <FormikProvider value={formik}>
                <CreateOrganizationDataProvider>
                    <OnboardingLayout
                        currentStep={currentStep}
                        nextStep={nextStep}
                        isExiting={isExiting}
                        main={main}
                        sidebar={sidebar}
                    />
                </CreateOrganizationDataProvider>
            </FormikProvider>
        </BaseOnboardingContainer>
    );
};
